import type { StoreActionApi } from 'react-sweet-state';
import { defineMessages, type IntlShape } from 'react-intl-next';

import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';

import type { SnippetsState } from '../snippetsStore.types.ts';
import * as BlockServiceClient from '../../services/blockServiceClient.ts';
import { reportError } from '../../reportError.ts';

import { showSnippetErrorFlag } from './actionsHelper.ts';

export interface FavoriteSnippetOptions {
	flags: WithFlagsProps['flags'];
	intl: IntlShape;
}

const messages = defineMessages({
	favoriteErrorFlagTitle: {
		id: 'snippets.favoriteActions.favoriteErrorFlagTitle',
		defaultMessage: 'Unable to favorite snippet',
		description: 'Title for error flag when favoriting a snippet fails',
	},
	favoriteErrorFlagDescription: {
		id: 'snippets.favoriteActions.favoriteErrorFlagDescription',
		defaultMessage: 'Please try again later.',
		description: 'Description for error flag when favoriting a snippet fails',
	},
	unfavoriteErrorFlagTitle: {
		id: 'snippets.favoriteActions.unfavoriteErrorFlagTitle',
		defaultMessage: 'Unable to unfavorite snippet',
		description: 'Title for error flag when unfavoriting a snippet fails',
	},
	unfavoriteErrorFlagDescription: {
		id: 'snippets.favoriteActions.unfavoriteErrorFlagDescription',
		defaultMessage: 'Please try again later.',
		description: 'Description for error flag when unfavoriting a snippet fails',
	},
});

export const favoriteActions = {
	favoriteSnippet:
		(snippetId: string, options?: FavoriteSnippetOptions) =>
		async ({ setState, getState }: StoreActionApi<SnippetsState>): Promise<boolean> => {
			const { snippets: previousSnippets } = getState();

			const newSnippetsState = previousSnippets.map((snippet) =>
				snippet.snippetId === snippetId ? { ...snippet, isFavorite: true } : snippet,
			);
			setState({ snippets: newSnippetsState });

			try {
				await BlockServiceClient.favoriteSnippet(snippetId);
				return true;
			} catch (error) {
				setState({ snippets: previousSnippets });

				const err = error instanceof Error ? error : new Error(String(error));
				reportError(err);

				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(messages.favoriteErrorFlagTitle),
						options.intl.formatMessage(messages.favoriteErrorFlagDescription),
					);
				}

				return false;
			}
		},

	unfavoriteSnippet:
		(snippetId: string, options?: FavoriteSnippetOptions) =>
		async ({ setState, getState }: StoreActionApi<SnippetsState>): Promise<boolean> => {
			const { snippets: previousSnippets } = getState();

			const newSnippetsState = previousSnippets.map((snippet) =>
				snippet.snippetId === snippetId ? { ...snippet, isFavorite: false } : snippet,
			);
			setState({ snippets: newSnippetsState });

			try {
				await BlockServiceClient.unfavoriteSnippet(snippetId);
				return true;
			} catch (error) {
				setState({ snippets: previousSnippets });

				const err = error instanceof Error ? error : new Error(String(error));
				reportError(err);

				if (options) {
					showSnippetErrorFlag(
						options.flags,
						options.intl.formatMessage(messages.unfavoriteErrorFlagTitle),
						options.intl.formatMessage(messages.unfavoriteErrorFlagDescription),
					);
				}

				return false;
			}
		},
};
